import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useAuth } from '../contexts/AuthContext';
import {
  getJobTreadOrganizations,
  getJobTreadCustomers,
  getJobTreadJobs,
  getJobTreadVendors
} from '../services/jobtreadService';
import { getUserIntegrationsStatus } from '../services/userIntegrationService';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { FaUsers, FaBriefcase, FaTruck, FaLink, FaSpinner } from 'react-icons/fa';
import PortalBackground from '../components/PortalBackground';

// Extract organizations from JobTread API response
const extractOrganizations = (data) => {
  if (!data) return [];
  const nodes = data?.currentGrant?.user?.memberships?.nodes || [];
  return nodes
    .map((n) => n?.organization)
    .filter(Boolean)
    .map((org) => ({ id: org.id, name: org.name }));
};

// Extract accounts (customers/vendors) from response
const extractAccounts = (data) => {
  if (!data) return [];
  const nodes = data?.organization?.accounts?.nodes || [];
  return nodes;
};

// Extract jobs from response
const extractJobs = (data) => {
  if (!data) return [];
  const nodes = data?.organization?.jobs?.nodes || [];
  return nodes;
};

const PortalJobTread = () => {
  const { darkMode } = useTheme();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [activeTab, setActiveTab] = useState('customers');
  const [customers, setCustomers] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [dataLoading, setDataLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check JobTread connection (portal users: users collection)
  useEffect(() => {
    const checkConnection = async () => {
      if (!currentUser) return;
      try {
        const userDoc = await getDoc(doc(db, 'users', currentUser.uid));
        const hasInUsers = userDoc.exists() && userDoc.data().integrations?.jobtread?.connected;
        const internalDoc = await getDoc(doc(db, 'internal_users', currentUser.uid));
        const hasInInternal = internalDoc.exists() && internalDoc.data().integrations?.jobtread?.connected;
        setConnected(!!(hasInUsers || hasInInternal));
      } catch (e) {
        console.error('Error checking JobTread connection:', e);
        setConnected(false);
      } finally {
        setLoading(false);
      }
    };
    checkConnection();
  }, [currentUser]);

  // Load organizations when connected
  useEffect(() => {
    if (!connected || !currentUser) return;
    const loadOrgs = async () => {
      try {
        setDataLoading(true);
        setError(null);
        const data = await getJobTreadOrganizations();
        const orgs = extractOrganizations(data);
        setOrganizations(orgs);
        if (orgs.length > 0 && !selectedOrgId) {
          setSelectedOrgId(orgs[0].id);
        }
      } catch (err) {
        console.error('Error loading organizations:', err);
        setError(err.message || 'Failed to load organizations');
      } finally {
        setDataLoading(false);
      }
    };
    loadOrgs();
  }, [connected, currentUser]);

  // Load tab data when org selected
  useEffect(() => {
    if (!selectedOrgId) return;
    const loadData = async () => {
      try {
        setDataLoading(true);
        setError(null);
        if (activeTab === 'customers') {
          const data = await getJobTreadCustomers(selectedOrgId);
          setCustomers(extractAccounts(data));
        } else if (activeTab === 'jobs') {
          const data = await getJobTreadJobs(selectedOrgId);
          setJobs(extractJobs(data));
        } else if (activeTab === 'vendors') {
          const data = await getJobTreadVendors(selectedOrgId);
          setVendors(extractAccounts(data));
        }
      } catch (err) {
        console.error(`Error loading ${activeTab}:`, err);
        setError(err.message || `Failed to load ${activeTab}`);
      } finally {
        setDataLoading(false);
      }
    };
    loadData();
  }, [selectedOrgId, activeTab]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-24">
        <FaSpinner className="animate-spin text-2xl text-red-500" />
      </div>
    );
  }

  if (!connected) {
    return (
      <div className={`p-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm`}>
        <h2 className={`text-xl font-semibold mb-4 ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          JobTread
        </h2>
        <div className={`p-6 rounded-xl border-2 border-dashed border-gray-300 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50`}>
          <p className={`text-center mb-4 ${darkMode ? 'text-gray-300' : 'text-gray-600'}`}>
            Connect your JobTread account to view and track your Customers, Jobs, and Vendors.
          </p>
          <div className="flex justify-center">
            <button
              onClick={() => navigate('/portal/profile')}
              className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md font-medium transition-colors"
            >
              Connect JobTread in Profile
            </button>
          </div>
        </div>
      </div>
    );
  }

  const tabs = [
    { key: 'customers', label: 'Customers', icon: FaUsers },
    { key: 'jobs', label: 'Jobs', icon: FaBriefcase },
    { key: 'vendors', label: 'Vendors', icon: FaTruck }
  ];

  const renderTable = (items, columns) => (
    <div className="overflow-x-auto">
      <table className="w-full">
        <thead>
          <tr className={`border-b ${darkMode ? 'border-gray-600' : 'border-gray-200'}`}>
            {columns.map((col) => (
              <th
                key={col.key}
                className={`px-4 py-3 text-left text-sm font-medium ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}
              >
                {col.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length}
                className={`px-4 py-8 text-center text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
              >
                No data found
              </td>
            </tr>
          ) : (
            items.map((item, i) => (
              <tr key={item.id || i} className={`border-b ${darkMode ? 'border-gray-700' : 'border-gray-100'}`}>
                {columns.map((col) => (
                  <td
                    key={col.key}
                    className={`px-4 py-3 text-sm ${darkMode ? 'text-gray-200' : 'text-gray-800'}`}
                  >
                    {col.render ? col.render(item) : item[col.key] ?? '-'}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );

  const customerColumns = [
    { key: 'name', label: 'Name' },
    { key: 'type', label: 'Type' },
    { key: 'id', label: 'ID' }
  ];
  const jobColumns = [
    { key: 'name', label: 'Name' },
    { key: 'number', label: 'Number' },
    { key: 'status', label: 'Status' },
    {
      key: 'account',
      label: 'Customer',
      render: (row) => row.account?.name ?? '-'
    }
  ];
  const vendorColumns = [
    { key: 'name', label: 'Name' },
    { key: 'type', label: 'Type' },
    { key: 'id', label: 'ID' }
  ];

  let tableData = [];
  let tableColumns = [];
  if (activeTab === 'customers') {
    tableData = customers;
    tableColumns = customerColumns;
  } else if (activeTab === 'jobs') {
    tableData = jobs;
    tableColumns = jobColumns;
  } else {
    tableData = vendors;
    tableColumns = vendorColumns;
  }

  return (
    <div className="relative min-h-screen p-6">
      <PortalBackground />
      <div className="relative p-6 rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 shadow-sm">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
        <h2 className={`text-xl font-semibold ${darkMode ? 'text-white' : 'text-gray-900'}`}>
          JobTread – Customers, Jobs & Vendors
        </h2>
        {organizations.length > 1 && (
          <select
            value={selectedOrgId}
            onChange={(e) => setSelectedOrgId(e.target.value)}
            className={`px-3 py-2 rounded-md border text-sm ${
              darkMode ? 'bg-gray-700 border-gray-600 text-white' : 'bg-white border-gray-300 text-gray-900'
            }`}
          >
            {organizations.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name}
              </option>
            ))}
          </select>
        )}
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-600">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            onClick={() => setActiveTab(tab.key)}
            className={`flex items-center gap-2 px-4 py-2 -mb-px font-medium text-sm transition-colors ${
              activeTab === tab.key
                ? darkMode
                  ? 'text-blue-400 border-b-2 border-blue-400'
                  : 'text-blue-600 border-b-2 border-blue-600'
                : darkMode
                  ? 'text-gray-400 hover:text-gray-300'
                  : 'text-gray-600 hover:text-gray-800'
            }`}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {error && (
        <div className={`mb-4 p-4 rounded-lg ${darkMode ? 'bg-red-900/30 text-red-300' : 'bg-red-50 text-red-800'}`}>
          {error}
        </div>
      )}

      {dataLoading ? (
        <div className="flex justify-center py-12">
          <FaSpinner className="animate-spin text-2xl text-red-500" />
        </div>
      ) : (
        <div className={`rounded-xl border border-gray-200 dark:border-gray-800`}>
          {renderTable(tableData, tableColumns)}
        </div>
      )}

      <p className={`mt-4 text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
        Data synced from your connected JobTread account. Connect or manage in{' '}
        <button
          onClick={() => navigate('/portal/profile')}
          className="text-blue-500 hover:underline inline-flex items-center gap-1"
        >
          Profile <FaLink className="w-3 h-3" />
        </button>
      </p>
      </div>
    </div>
  );
};

export default PortalJobTread;
